import React from 'react'
import Link from '@3mcom/mds-library/dist/node/Link'
import CtaTypeEnum from '../../../common/constants/CtaTypeEnum'
import { useIsDesktop } from '../../services/UtilityService'
import LightboxCTA from './LightboxCTA'
import scrollService from '../../services/ScrollService'

/**
 * ProductOptionCTA
 *
 * Renders the CTA button for a single row in the SKU / Product Options table.
 * Each row only shows its highest-priority CTA (index 0 of ctaButtons, already sorted server-side).
 *
 * Rendering is split into three paths:
 *   1. Lightbox CTAs  - form CTAs where hasLightbox=true open a popup inline via LightboxCTA.
 *   2. Form scroll CTAs - non-lightbox form CTAs (Lead Form, Contact Acquisition, etc.) stay on
 *                         the base PDP and scroll to the form section using scrollService.
 *                         Base and variant forms share the same OID, so variant redirect is not needed.
 *   3. Standard link CTAs - WTB navigates to the variant PDP; Call to Action opens its own URL.
 *
 * Exports buildCtaStyleMap and getFirstCTA for use by the parent ProductOptions component.
 */

const STYLE_TYPE_MAP = {
    primary: 'mds-button_primary',
    secondary: 'mds-button_secondary',
    tertiary: 'mds-button_tertiary'
}

// Resolves the href for standard link-based CTAs.
// Form types are intentionally absent here - they are handled by the scroll path below.
const CTA_URL_RESOLVERS = {
    [CtaTypeEnum.WTB]: (ctaButton, product) => product.productDetails?.url,
    [CtaTypeEnum.CALL_TO_ACTION]: (ctaButton) => ctaButton.url
}

// Form CTAs that should scroll to their section on the base PDP instead of navigating away.
// Base and variant forms share the same OID; the form captures the page URL on submission,
// so there is no need to redirect the user to the variant PDP first.
const FORM_SCROLL_CTA_TYPES = new Set([
    CtaTypeEnum.LEAD_FORM_1,
    CtaTypeEnum.LEAD_FORM_2,
    CtaTypeEnum.CONTACT_ACQUISITION,
    CtaTypeEnum.POST_PURCHASE_FORM
])

/**
 * Returns the first CTA button object from ctaButtons array (already sorted by priority).
 * @param {Object} product - Product option object
 * @returns {Object|null} - First CTA button object or null
 */
const getFirstCTA = (product) => {
    if (Array.isArray(product.ctaButtons) && product.ctaButtons.length > 0) {
        return product.ctaButtons[0]
    }
    return null
}

const STYLE_ORDER = ['primary', 'secondary', 'tertiary']

/**
 * Builds a Map of snaps2Id → styleType based on the order distinct CTA types
 * first appear across product options.
 *
 * Rules:
 *   - 1st distinct snaps2Id → 'primary'
 *   - 2nd distinct snaps2Id → 'secondary'
 *   - 3rd+ distinct snaps2Id → 'tertiary'
 *   - Products without a CTA are skipped and do not advance the rank
 *   - Products sharing a snaps2Id inherit the style assigned on first encounter
 *
 * @param {Array} productOptions - Array of product option objects
 * @returns {Map<string, string>} - Map of snaps2Id to styleType
 */
const buildCtaStyleMap = (productOptions) => {
    const styleMap = new Map()
    if (!Array.isArray(productOptions)) return styleMap

    let rank = 0
    productOptions.forEach((product) => {
        const cta = getFirstCTA(product)
        if (!cta) return

        const { snaps2Id } = cta
        if (styleMap.has(snaps2Id)) return

        styleMap.set(snaps2Id, STYLE_ORDER[Math.min(rank, STYLE_ORDER.length - 1)])
        rank++
    })

    return styleMap
}

/**
 * Builds the MDS button class string for a given style type and viewport.
 * @param {string} styleType - Button style variant: 'primary' | 'secondary' | 'tertiary'
 * @param {boolean} isDesktop - Whether the viewport is desktop-sized
 * @returns {string} - Space-separated CSS class string
 */
const buildButtonClass = (styleType, isDesktop) => {
    const resolvedStyle = STYLE_TYPE_MAP[styleType] ?? 'mds-button_primary'
    return ['mds-button', resolvedStyle, !isDesktop && `${resolvedStyle}--small`]
        .filter(Boolean)
        .join(' ')
}

/**
 * Renders a styled CTA link button.
 * @param {string} ctaUrl - URL for the link
 * @param {string} ctaLabel - Button label text
 * @param {string} styleType - Button style variant: 'primary' | 'secondary' | 'tertiary'
 * @param {boolean} isDesktop - Whether the viewport is desktop-sized
 * @param {Function} [onClick] - Optional click handler (e.g. for scroll-to-section)
 */
const CTAButton = ({ ctaUrl, ctaLabel, styleType, isDesktop, onClick }) => {
    const styleClass = buildButtonClass(styleType, isDesktop)

    return (
        <div className="sps2-pdp_product-options--cta">
            <Link
                className={styleClass}
                href={ctaUrl}
                onClick={onClick}
                tealiumObj={{
                    event_name: `PDP Buying Zone Product Options Table CTA Button Click: ${ctaLabel}`,
                    event_type: 'Engagement Event',
                    experience_type: 'SNAPS2'
                }}
            >
                {ctaLabel}
            </Link>
        </div>
    )
}

/**
 * Renders a styled anchor that triggers the flying Commerce Connector Service Locator
 * dialog. The cc-sf-button CSS class is the hook registered by the CC widget script.
 * @param {string} ctaLabel - Button label text
 * @param {string} styleType - Button style variant: 'primary' | 'secondary' | 'tertiary'
 * @param {boolean} isDesktop - Whether the viewport is desktop-sized
 */
const ServiceLocatorCTAButton = ({ ctaLabel, styleType, isDesktop }) => {
    const styleClass = buildButtonClass(styleType, isDesktop)

    return (
        <div className="sps2-pdp_product-options--cta">
            <a
                className={`cc-sf-button ${styleClass}`}
                href="#servicelocator"
                aria-label={ctaLabel}
            >
                <span>{ctaLabel}</span>
            </a>
        </div>
    )
}

/**
 * Renders the primary CTA button for a product option based on its CTA type.
 * @param {Object} value - Product option object containing ctaButtons
 * @param {string} styleType - Button style variant
 */
const ProductOptionCTA = ({ value, styleType }) => {
    const isDesktop = useIsDesktop()
    const ctaButton = getFirstCTA(value)
    if (!ctaButton) return null

    if (ctaButton.snaps2Id === CtaTypeEnum.SERVICE_LOCATOR) {
        return (
            <ServiceLocatorCTAButton
                ctaLabel={ctaButton.label}
                styleType={styleType}
                isDesktop={isDesktop}
            />
        )
    }

    // Lightbox CTAs embed the form HTML on the page - clicking opens a popup, no navigation needed.
    if (ctaButton.hasLightbox && ctaButton.html) {
        return (
            <div className="sps2-pdp_product-options--cta">
                <LightboxCTA
                    cta={ctaButton}
                    buttonClass={buildButtonClass(styleType, isDesktop)}
                />
            </div>
        )
    }

    if (FORM_SCROLL_CTA_TYPES.has(ctaButton.snaps2Id)) {
        return (
            <CTAButton
                // ctaUrl={`#sps2-pdp_${ctaButton.snaps2Id}`}
                ctaUrl={`${value.productDetails?.url}#${ctaButton.snaps2Id}`}
                ctaLabel={ctaButton.label}
                styleType={styleType}
                isDesktop={isDesktop}
                // onClick={(e) => {
                //     // Prevent native anchor jump; use scrollService so sticky nav offset is respected.
                //     e.preventDefault()
                //     scrollService.scrollToSection(`sps2-pdp_${ctaButton.snaps2Id}`)
                // }}
            />
        )
    }

    const resolveUrl = CTA_URL_RESOLVERS[ctaButton.snaps2Id]
    if (!resolveUrl) return null

    return (
        <CTAButton
            ctaUrl={resolveUrl(ctaButton, value)}
            ctaLabel={ctaButton.label}
            styleType={styleType}
            isDesktop={isDesktop}
        />
    )
}

export { getFirstCTA, buildCtaStyleMap }
export default ProductOptionCTA
